import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/contexts/UserContext';
import { notificationsService } from '@/services/notificationsService';
import { useWebSocket } from '@/hooks/useWebSocket';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  action_url?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useUser();
  const { on, connected } = useWebSocket();

  // Load notifications (only unread)
  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      // apiService.get() returns data directly (unwrapped from response.data.data)
      const response = await notificationsService.getUnread();
      // Response can be: array, { items: [...] }, { notifications: [...] }, or null
      const items = Array.isArray(response) 
        ? response 
        : (response?.items || response?.notifications || []);
      setNotifications(Array.isArray(items) ? items : []);
      setUnreadCount(Array.isArray(items) ? items.length : 0);
    } catch (error) {
      console.error('(Hook) Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Refresh function that can be called externally
  const refresh = useCallback(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationsService.markAsRead(notificationId);
      // Remove from list since we only show unread notifications
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }, []);

  // Delete all notifications
  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return false;

    try {
      await notificationsService.markAllAsRead();

      // Immediately update the UI
      setNotifications([]);
      setUnreadCount(0);
      
      // Also reload notifications to ensure sync
      setTimeout(() => {
        loadNotifications();
      }, 100);
      
      return true;
    } catch (error) {
      console.error('Error marking all as read:', error);
      return false;
    }
  }, [user?.id, loadNotifications]);

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    if (!user?.id || !connected) return;

    const cleanupNew = on('notification:new', (notification: Notification) => {
      console.log('New notification received:', notification);
      // Only add if it's unread
      if (!notification.read) {
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
      }
    });

    const cleanupRead = on('notification:read', (data: { notificationId: string }) => {
      console.log('Notification marked as read:', data);
      // Remove from list if it becomes read
      setNotifications(prev => 
        prev.filter(n => n.id !== data.notificationId)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    const cleanupDeleted = on('notification:deleted', (data: { notificationId: string }) => {
      console.log('Notification deleted:', data);
      setNotifications(prev => 
        prev.filter(n => n.id !== data.notificationId)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    });

    return () => {
      cleanupNew();
      cleanupRead();
      cleanupDeleted();
    };
  }, [user?.id, connected, on]);

  // Load notifications on mount
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  return {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    markAllAsRead,
    reload: loadNotifications,
    refresh
  };
};
